/**
 * Sonnerie d'appel ENTRANT — synthétisée, sans fichier audio embarqué.
 *
 * Jusqu'ici un appel entrant n'émettait AUCUN son : l'agent devait voir le
 * panneau pour s'apercevoir qu'on l'appelait. On joue donc une sonnerie tant
 * que l'appel sonne, sur le haut-parleur choisi dans Réglages, à un volume
 * propre à l'agent (distinct du volume de conversation, qu'il ne doit pas
 * perturber).
 *
 * Motif « double sonnerie » : deux brèves salves de deux fréquences proches
 * (440 + 480 Hz, le battement donne le timbre téléphonique classique), puis un
 * silence. Les rampes de gain évitent le « clic » d'un démarrage carré.
 */

export const RING_VOLUME_KEY = 'pexitalk:ringVolume'
export const RING_VOLUME_DEFAULT = 70

/** Motif de la sonnerie : salves (début, durée) et période, en secondes. */
const BURSTS: { at: number; dur: number }[] = [
  { at: 0, dur: 0.38 },
  { at: 0.55, dur: 0.38 },
]
const PERIOD_S = 3.2
const TONES_HZ = [440, 480]
const RAMP_S = 0.02
/** Plafond du gain : deux oscillateurs sommés saturent vite. */
const PEAK_GAIN = 0.22

type SinkSettable = { setSinkId?: (id: string) => Promise<void> }

/** Borne un volume de sonnerie (pourcentage entier 0-100). */
export function clampRingVolume(value: unknown): number {
  // Une valeur absente ne doit JAMAIS être lue comme 0 : `Number(null)` vaut 0
  // et rendrait la sonnerie muette au moindre réglage manquant.
  if (value === null || value === undefined || value === '') return RING_VOLUME_DEFAULT
  const n = Number(value)
  if (!Number.isFinite(n)) return RING_VOLUME_DEFAULT
  return Math.round(Math.min(100, Math.max(0, n)))
}

export function loadRingVolume(): number {
  try {
    const raw = localStorage.getItem(RING_VOLUME_KEY)
    if (raw === null) return RING_VOLUME_DEFAULT
    return clampRingVolume(raw)
  } catch {
    return RING_VOLUME_DEFAULT   // localStorage indisponible
  }
}

export function saveRingVolume(pct: number): void {
  try { localStorage.setItem(RING_VOLUME_KEY, String(clampRingVolume(pct))) }
  catch { /* localStorage indisponible */ }
}

export class Ringtone {
  private ctx: AudioContext | null = null
  private gain: GainNode | null = null
  private oscs: OscillatorNode[] = []
  private timer: ReturnType<typeof setInterval> | null = null

  get playing(): boolean { return this.ctx !== null }

  /**
   * Démarre la sonnerie en boucle. Idempotent : un second appel ne fait
   * qu'ajuster le volume. `volumePct` 0 = silencieux (on ne démarre rien).
   * `sinkId` vide = haut-parleur système.
   */
  async start(volumePct: number, sinkId = ''): Promise<void> {
    const vol = clampRingVolume(volumePct)
    if (this.ctx) { this.setVolume(vol); return }
    if (vol === 0) return
    try {
      const Ctx = window.AudioContext
        || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      const ctx = new Ctx()
      const sinkable = ctx as unknown as SinkSettable
      if (sinkId && typeof sinkable.setSinkId === 'function') {
        // Best-effort : identifiant périmé ou navigateur sans routage de sortie.
        try { await sinkable.setSinkId(sinkId) } catch { /* haut-parleur système */ }
      }
      try { await ctx.resume() } catch { /* déjà actif */ }
      const gain = ctx.createGain()
      gain.gain.value = 0
      gain.connect(ctx.destination)
      const oscs = TONES_HZ.map((hz) => {
        const osc = ctx.createOscillator()
        osc.type = 'sine'
        osc.frequency.value = hz
        osc.connect(gain)
        osc.start()
        return osc
      })
      this.ctx = ctx
      this.gain = gain
      this.oscs = oscs
      this.setVolume(vol)
      this.cycle()
      this.timer = setInterval(() => this.cycle(), PERIOD_S * 1000)
    } catch (e) {
      console.warn('[PexiTalk] sonnerie indisponible', e)
      this.stop()
    }
  }

  /** Ajuste le volume en direct (aperçu dans Réglages, pendant la sonnerie). */
  setVolume(volumePct: number): void {
    this.peak = (clampRingVolume(volumePct) / 100) * PEAK_GAIN
  }

  stop(): void {
    if (this.timer) { clearInterval(this.timer); this.timer = null }
    for (const osc of this.oscs) {
      try { osc.stop() } catch { /* déjà arrêté */ }
      try { osc.disconnect() } catch { /* ignore */ }
    }
    this.oscs = []
    try { this.gain?.disconnect() } catch { /* ignore */ }
    try { void this.ctx?.close() } catch { /* ignore */ }
    this.gain = null
    this.ctx = null
  }

  private peak = (RING_VOLUME_DEFAULT / 100) * PEAK_GAIN

  /** Programme une période du motif sur l'horloge audio (précise, non bloquée
   *  par le thread principal). */
  private cycle(): void {
    const ctx = this.ctx
    const gain = this.gain
    if (!ctx || !gain) return
    const t0 = ctx.currentTime
    const g = gain.gain
    for (const b of BURSTS) {
      const on = t0 + b.at
      g.setValueAtTime(0, on)
      g.linearRampToValueAtTime(this.peak, on + RAMP_S)
      g.setValueAtTime(this.peak, on + b.dur - RAMP_S)
      g.linearRampToValueAtTime(0, on + b.dur)
    }
  }
}
